import ActionPerformer from "@/utils/moreMenu/ActionPerformer.ts";
import {ActionPlaceholder} from "@/utils/moreMenu/ActionHandler.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import ActionPerformerUtil from "@/utils/moreMenu/ActionPerformerUtil.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import {Dialog} from "quasar";
import {defineAsyncComponent} from "vue";
import {DashboardDialogPayload, GUIActionDoneCallback} from "@/utils/presenters/interfaces.ts";
import Translator from "@/utils/common/Translator.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import NeoleapResponseUtil from "@/utils/NeoleapResponseUtil.ts";
import GeideaResponseUtil from "@/utils/GeideaResponseUtil.ts";
import {SimpleEntityMenuItem} from "@/utils/UIConstants/MoreMenuItem.ts";
import CustomReportLauncherAction from "@/utils/editView/editScreens/CustomReportLauncherAction.ts";
import ACCEntities from "@/utils/constants/ACCEntities.ts";
import FieldToFocusUtils from "@/utils/common/FieldToFocusUtils.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";

const DashboardEditorDialog = defineAsyncComponent(() => import("@/components/dialogs/DashboardEditorDialog.vue"));
const ChartConfigEditorDialog = defineAsyncComponent(() => import("@/components/bi/ChartConfigEditorDialog.vue"));
const VirtualEntityEditorDialog = defineAsyncComponent(() => import("@/components/virtualEntity/VirtualEntityEditorDialog.vue"));
import PerformActionReq = namaTypes.PerformActionReq;
import EInvoiceSignResponse = namaTypes.EInvoiceSignResponse;

export default class EditViewPerformer extends ActionPerformer
{
  private presenter: EditPresenter;

  constructor(presenter: EditPresenter) {
    super();
    this.presenter = presenter;
  }

  override async calcDefaultsOf(action: ActionPlaceholder, callback: (fields: namaTypes.DTOFilledQuestionField[]) => void) {
    await ActionPerformerUtil.calcDefaultsOf(action, callback, this.presenter.dispatcher!);
  }

  override getSearchContextProvider(): BaseDispatcher | undefined {
    return this.presenter.dispatcher!;
  }

  override isRecordNotSaved(): boolean {
    return this.presenter.isInNewMode() || this.presenter.hasChanges();
  }

  override editDashboardElements(): void {
    const dashboardRecord = this.presenter.fetchRecord();
    const kind = NamaCommon.getFieldValue(dashboardRecord, 'kind');
    if (kind === 'Tabbed') {
      useErrorsStore().pushErrorStr(Translator.translate('biTabbedDashboardCannotHaveWidgets'));
      return;
    }
    Dialog.create({
      component: DashboardEditorDialog,
      componentProps: {
        dashboardRecord: dashboardRecord
      }
    }).onOk(async (payload: DashboardDialogPayload) => {
      const record = this.presenter.fetchRecord();
      NamaCommon.setFieldValue(record, 'rowsCount', payload.row);
      NamaCommon.setFieldValue(record, 'colsCount', payload.column);
      NamaCommon.setFieldValue(record, 'totalDashboardRowsCount', payload.rowsCount);

      const oldCharts: any[] = NamaCommon.getFieldValue(record, 'charts') ?? [];
      const oldByWidgetId = new Map<string, any>();
      for (const row of oldCharts)
        if (row?.element?.id)
          oldByWidgetId.set(row.element.id, row);

      const newCharts: any[] = [];
      for (const el of payload.elements) {
        let row = oldByWidgetId.get(el.id);
        if (!row) {
          const newId = await ClientCache.asyncNextGUIDId();
          row = {
            id: newId,
            g2Id: newId,
            element: {
              id: el.id,
              code: el.widgetCode,
              name1: el.widgetName1,
              name2: el.widgetName2,
              entityType: 'DashBoardWidget',
            },
          };
        }
        row.columnNumber = el.column;
        row.rowNumber = el.row;
        row.widthInColumns = el.columnSpan;
        row.heightInRows = el.rowSpan;
        newCharts.push(row);
      }
      NamaCommon.setFieldValue(record, 'charts', newCharts);
    });
  }

  override editChartConfig(): void {
    const record = this.presenter.fetchRecord();
    Dialog.create({
      component: ChartConfigEditorDialog,
      componentProps: {
        widgetRecord: record,
        dashboardId: NamaCommon.getFieldValue(record, 'dashboard') ?? ''
      }
    }).onOk((chartConfigJSON: string) => {
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'chartConfigJSON', chartConfigJSON);
    });
  }

  override editMappings(): void {
    const record = this.presenter.fetchRecord();
    Dialog.create({
      component: VirtualEntityEditorDialog,
      componentProps: {
        entityRecord: record,
      }
    }).onOk((payload: {sqlQuery: string; mappingJson: string}) => {
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'sqlQuery', payload.sqlQuery);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'mappingJson', payload.mappingJson);
    });
  }

  override async fetchLastTerminalPayTrnx(): Promise<void> {
    await this.pgwTransaction(false);
  }

  override async payInvoice(paymentValue: number): Promise<void> {
    await this.pgwTransaction(true, paymentValue);
  }

  private async pgwTransaction(payment: boolean, paymentValue?: number) {
    let port = "7842";
    if (!NamaCommon.getFieldValue(this.presenter.fetchRecord(), "paymentLines"))
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), "paymentLines", []);
    let paymentLines: Record<string, any>[] = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "paymentLines");
    let line = await this.fetchOrAddPaymentLine(paymentLines);
    let valueToPaid: number = paymentValue || line.paymentValue || NamaCommon.getFieldValue(this.presenter.fetchRecord(), this.calcAmountFieldByEntityType());
    if (ObjectChecker.isNullOrUndefined(valueToPaid) || valueToPaid < 0)
      return;
    if (payment) {
      let pgwTransactionId = await ClientCache.asyncNextGUIDId() ?? ""
      localStorage.setItem('nm-last-pg-Id', pgwTransactionId);
      await this.handlePGWRequest(payment, pgwTransactionId, port, valueToPaid, paymentLines, line);
    } else {
      let lastPgwTransactionId: string = localStorage.getItem('nm-last-pg-Id') ?? '';
      await this.handlePGWRequest(payment, lastPgwTransactionId, port, valueToPaid, paymentLines, line);
    }
  }

  private calcAmountFieldByEntityType() {
    if (ObjectChecker.isAnyEqualToFirst(this.presenter.entityType, ACCEntities.PaymentVoucher, ACCEntities.PaymentOrder,
      ACCEntities.ReceiptVoucher, ACCEntities.ReceiptOrder, ACCEntities.PaymentOrder, ACCEntities.BankTransfer))
      return "amount.value.amount";
    return "money.remaining";
  }

  private async handlePGWRequest(payment: boolean, lastPgwTransactionId: string, port: string, valueToPaid: number, paymentLines: Record<string, any>[], line: Record<string, any>) {
    if (!payment && !lastPgwTransactionId) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no uncompleted transaction "));
      return;
    }
    let paymentTerminalRef = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "paymentTerminal");
    if (!paymentTerminalRef) {
      useErrorsStore().pushErrorStr(Translator.translateId("Please select payment terminal"));
      return;
    }
    if (await this.handleDeviceTerminalRequest(payment, lastPgwTransactionId, valueToPaid, paymentLines, line, paymentTerminalRef, port))
      return;
    let url = "http://localhost:" + port + "/payInvoice/" + valueToPaid + "/" + lastPgwTransactionId;
    if (!payment)
      url = "http://localhost:" + port + "/lastTrnx/" + lastPgwTransactionId;
    try {
      let response: any = await NamaCommon.namaFetch(url, {doNotUseMainReqForHttp: true,
        method : 'get'});
      console.log('handlePGWRequest', response);
      await this.processPayResponse(response, valueToPaid, paymentLines.indexOf(line), port);
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port
        + ", please make sure that the PGW server is running. You can download the setup file from <a href='https://www.namasoft.com/bin/PGWSetup.msi' target='_blank'>here</a>"
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }

  /**
   * Handles terminals that are an Android device running nama_pos as a payment gateway.
   *
   * The tap happens on the device, so nothing here can wait on a single request: the payment is handed to the device,
   * the device reports back to the server, and this polls until the outcome is in. Returns false for a Windows PGW
   * terminal so the caller keeps talking to localhost as before.
   */
  private async handleDeviceTerminalRequest(payment: boolean, trxRef: string, valueToPaid: number,
                                            paymentLines: Record<string, any>[], line: Record<string, any>,
                                            paymentTerminalRef: any, port: string): Promise<boolean> {
    let customer = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "customer");
    let started: any;
    try {
      started = await NamaCommon.namaFetch(NamaURLS.StartTerminalPayment, {
        params: {
          terminal: JSON.stringify(paymentTerminalRef),
          trxRef: trxRef,
          valueToPaid: valueToPaid,
          recover: !payment,
          // The device posts its result back here. Sent from the browser because the server has no reliable way of
          // knowing which of its addresses the shop network can reach it on.
          callbackUrl: this.calcPGWCallbackURL(),
          customerReference: customer?.id ?? "",
          browserCallsDevice: true,
        }
      });
    } catch (e) {
      return false;
    }
    if (!started || started.provider !== 'nearpay')
      return false;
    let result = started;
    if (started.deviceUrl && started.status !== 'failed')
      result = await this.driveDeviceTerminal(payment, started.deviceUrl, trxRef, valueToPaid, customer);
    if (result?.status === 'pending')
      result = await this.awaitDeviceTerminalPayment(trxRef);
    if (!result || result.status !== 'done') {
      useErrorsStore().pushErrorStr(Translator.translateId(result?.error || "Purchase Transaction not completed successfully"));
      return true;
    }
    await this.processPayResponse(JSON.parse(result.pgwResp), valueToPaid, paymentLines.indexOf(line), port);
    return true;
  }

  /**
   * Starts the payment on a terminal only this browser can reach, which is the case whenever Nama is served from
   * outside the shop: a device on the shop network has no address a public server can open a connection to, so the
   * cashier runs Nama on the terminal itself and it is reached here over loopback.
   *
   * The device still posts its receipt to the server, so everything after this point is the same as for a terminal the
   * server drives.
   */
  private async driveDeviceTerminal(payment: boolean, deviceUrl: string, trxRef: string, valueToPaid: number,
                                    customer: any): Promise<any> {
    if (!payment) {
      let stored = await this.callDevice(deviceUrl, '/lastTrnx/' + trxRef);
      if (stored.status === 'failed')
        return stored;
      return stored.httpStatus === 200 ? await this.completeFromDevice(trxRef, stored) : this.deviceRefusal(stored);
    }
    let answer = await this.callDevice(deviceUrl, '/payInvoice', {
      trxRef: trxRef,
      amount: valueToPaid,
      callbackUrl: this.calcPGWCallbackURL(),
      customerReference: customer?.id ?? "",
    });
    if (answer.status === 'failed')
      return answer;
    if (answer.accepted)
      return {status: 'pending'};
    // A device answering with a transaction it had already finished is replaying a request whose callback went
    // missing. Its receipt still has to be translated by the server before it can reach the document.
    if (answer.alreadyProcessed)
      return await this.completeFromDevice(trxRef, answer);
    return this.deviceRefusal(answer);
  }

  /**
   * Talks to nama_pos itself. Uses fetch rather than namaFetch because the device is not a Nama server: it must not be
   * sent session parameters, and its own status code is what decides what happens next. A device that cannot be
   * reached at all answers here rather than throwing, so a failure of the ERP call that follows is not mistaken for it.
   */
  private async callDevice(deviceUrl: string, path: string, body?: any): Promise<any> {
    try {
      let response = await fetch(deviceUrl + path, body === undefined ? {method: 'GET'} : {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body),
      });
      return {httpStatus: response.status, ...(await response.json().catch(() => ({})))};
    } catch (e) {
      return {
        status: 'failed',
        error: Translator.translateId("Cannot reach the payment device on " + deviceUrl
          + ", please make sure nama_pos is open on this device in payment gateway mode")
      };
    }
  }

  private deviceRefusal(answer: any): any {
    return {
      status: 'failed',
      error: answer.error || Translator.translateId("Payment device refused the request, HTTP " + answer.httpStatus)
    };
  }

  /**
   * Hands the server a receipt read from the device, so it is translated and matched exactly like a posted one. The
   * receipt travels in the body, as it does when the device posts it itself: it is far too big for a query string.
   */
  private async completeFromDevice(trxRef: string, devicePayload: any): Promise<any> {
    return await NamaCommon.namaFetch(NamaURLS.CompleteTerminalPayment, {
      params: {trxRef: trxRef},
      data: devicePayload,
    });
  }

  private calcPGWCallbackURL(): string {
    return new URL('pgwcallback', window.location.href).href;
  }

  private async awaitDeviceTerminalPayment(trxRef: string): Promise<any> {
    const pollEveryMs = 2000;
    // nama_pos cancels a payment nobody tapped after 60 seconds and reports it; the extra time lets that report land.
    // Measured by the clock because Chrome slows timers while the browser sits behind nama_pos.
    const giveUpAfterMs = 90 * 1000;
    const startedAt = Date.now();
    useLoadingStore().startLoading();
    try {
      while (Date.now() - startedAt < giveUpAfterMs) {
        await new Promise(resolve => setTimeout(resolve, pollEveryMs));
        try {
          let status: any = await NamaCommon.namaFetch(NamaURLS.CheckTerminalPayment, {params: {trxRef: trxRef}});
          if (status && status.status !== 'pending')
            return status;
        } catch (e) {
          // A dropped poll is not a failed payment; keep waiting for the device.
        }
      }
    } finally {
      useLoadingStore().stopLoading();
    }
    return {
      status: 'failed',
      error: "The payment device did not report a result. Use Fetch Last Terminal Payment Transaction to recover it."
    };
  }

  private async processPayResponse(pgwResp: any, valueToPaid: number, selectedRow: number, port: string) {
    let paymentTerminalRef = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "paymentTerminal");
    let request = {pgwResp: JSON.stringify(pgwResp), valueToPaid: valueToPaid, terminal: JSON.stringify(paymentTerminalRef)};
    try {
      let respLine: any = await NamaCommon.namaFetch(NamaURLS.PreparePGWLine, {params: request});
      console.log('processPayResponse', respLine);
      if (!respLine)
        return;
      let line = {
        merchantId: respLine.pgwProperties.merchantId,
        ecrRefNum: respLine.pgwProperties.ecrRefNum,
        schemeId: respLine.pgwProperties.schemeId,
        panNum: respLine.pgwProperties.panNum,
        terminalId: respLine.pgwProperties.terminalId,
        stanNum: respLine.pgwProperties.stanNum,
        authCode: respLine.pgwProperties.authCode,
        respCode: respLine.pgwProperties.respCode,
        de55Response: respLine.pgwProperties.de55Response,
        cardType: respLine.pgwProperties.cardType,
        maskedCardNumber: respLine.pgwProperties.maskedCardNumber,
        paidFromTerminal: respLine.pgwProperties.paidFromTerminal
      };
      let failureMsg = "Purchase Transaction refused";
      let failureRespCode = false;
      if (line.respCode) {
        let refuseReason = line.respCode.length == 2 ? NeoleapResponseUtil.respCodeWithErrorMsg(line.respCode) :
          GeideaResponseUtil.respCodeWithErrorMsg(line.respCode);
        if (refuseReason) {
          failureRespCode = true;
          failureMsg += " - " + line.respCode + " - " + refuseReason;
        }
      }
      if (failureRespCode || (!line.cardType && (!line.authCode || /^0+$/.test(line.authCode)))) {
        useErrorsStore().pushErrorStr(Translator.translateId(failureMsg));
        return;
      }
      if (ObjectChecker.areAllEmptyOrNull(line.merchantId, line.ecrRefNum, line.schemeId, line.terminalId, line.stanNum, line.authCode, line.panNum, line.de55Response, line.cardType, line.maskedCardNumber)) {
        useErrorsStore().pushErrorStr(Translator.translateId("Purchase Transaction not completed successfully"));
        return;
      }
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.merchantId', line.merchantId, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.ecrRefNum', line.ecrRefNum, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.panNum', line.panNum, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.schemeId', line.schemeId, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.terminalId', line.terminalId, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.de55Response', line.de55Response, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.cardType', line.cardType, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.maskedCardNumber', line.maskedCardNumber, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.stanNum', line.stanNum, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.pgwProperties.paidFromTerminal', line.paidFromTerminal, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.authorizationNumber', line.authCode, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.paymentMethod', respLine.paymentMethod, selectedRow);
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), 'paymentLines.paymentValue', valueToPaid, selectedRow);
      useLoadingStore().startLoading();
      await this.presenter.fetchDispatcher().validateColumn('paymentLines.paymentValue', selectedRow, false);
      await FieldToFocusUtils.focusToField(new FieldToFocusUtils(this.presenter), "paymentLines", IdsAndConstants.MRN_REFRESH_CELLS);
      useLoadingStore().stopLoading();
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port + ", please make sure that the PGW server is running. You can download the setup file from <a href='https://www.namasoft.com/bin/PGWSetup.msi' target='_blank'>here</a>"
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }

  /**
   * The last line is reused only while it has no payment value. A line that already records a payment, from a terminal or
   * entered by hand, is kept and a new line is added.
   */
  private async fetchOrAddPaymentLine(paymentLines: Record<string, any>[]): Promise<Record<string, any>> {
    let lastLine = paymentLines[paymentLines.length - 1];
    if (lastLine && ObjectChecker.isEmptyOrNull(lastLine.paymentValue))
      return lastLine;
    let id = await ClientCache.asyncNextGUIDId();
    paymentLines.push({id, g2Id: id});
    return paymentLines[paymentLines.length - 1];
  }

  override async readFirstWeight(): Promise<void> {
    await this.readScaleWeight(true);
  }

  override async readSecondWeight(): Promise<void> {
    await this.readScaleWeight(false);
  }

  private async readScaleWeight(readFirstWeight: boolean) {
    let port = "9494";
    let url = "http://localhost:" + port + "/readScaleWeight";
    try {
      const data = await fetch(url);
      const response = await data.json();
      console.log('readScaleWeight', response);
      await this.processReadScaleWeightResponse(readFirstWeight, response, port);
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port
        + ", please make sure that the Weight Scale server is running."
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }

  private async processReadScaleWeightResponse(readFirstWeight: boolean, weightRes: any, port: string) {
    try {
      let fieldId: any = "details.grossWeight";
      if (!readFirstWeight)
        fieldId = "details.emptyWeight";
      let details: Record<string, any>[] = NamaCommon.getFieldValue(this.presenter.fetchRecord(), "details");
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), fieldId, weightRes, details.length - 1);
      useLoadingStore().startLoading();
      await this.presenter.fetchDispatcher().validateColumn(fieldId, details.length - 1, false);
      useLoadingStore().stopLoading();
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port + ", please make sure that the Weight Scale server is running."
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }

  override async performAction(action: ActionPlaceholder, questionFields: namaTypes.DTOFilledQuestionField[]
    , onDone?: GUIActionDoneCallback): Promise<boolean> {
    return await ActionPerformerUtil.performAction(action, questionFields, this.presenter.metadata!, this.presenter.dispatcher!, onDone)
  }

  override async runCustomGuiPostAction(action: ActionPlaceholder): Promise<void> {
    if (ObjectChecker.isEmptyOrNull(action.customization?.guiPostActionsId))
      return;
    await this.presenter.dispatcher!.runManualGuiPostAction(action.customization!.performActionAsField ?? '',
      action.customization!.guiPostActionsId!);
  }

  override async runGuiPostActionForSystemAction(action: ActionPlaceholder): Promise<void> {
    await this.presenter.dispatcher!.runGuiPostActionForSystemAction(action.id ?? '');
  }

  override async runCustomAction(action: ActionPlaceholder): Promise<boolean> {
    //   in view
    if (!action.customization)
      return false;
    if (action.customization.guiPostActionsId) {
      await this.runCustomGuiPostAction(action);
      return true;
    }
    const errorsCountBeforeAction = useErrorsStore().errors.length;
    if (action.customization.reportInfo && action.customization.reportInfo.reportId) {
      let menuItem = new CustomReportLauncherAction(this.presenter, action.customization.reportInfo);
      if (menuItem.executeFn)
        menuItem.executeFn();
      return useErrorsStore().errors.length === errorsCountBeforeAction;
    }
    if (this.isRecordNotSaved()) {
      useErrorsStore().pushErrorStr(Translator.translateId("recordMustBeSaved"));
      return false;
    }
    let reqData: PerformActionReq = {
      actionInfo: action.customization,
      targetRecords: [{
        entityType: this.presenter.entityType,
        id: NamaCommon.getFieldValue(this.presenter.fetchRecord(), CommonFieldIds.ID, 0)
      }]
    };
    let result = await NamaCommon.namaFetch(NamaURLS.PERFORM_Custom_action, {data: reqData});
    if (result && result.data)
      await this.presenter.onCustomActionSuccess(result.data)
    return useErrorsStore().errors.length === errorsCountBeforeAction;
  }

  override async signDocuments(onlySelected: boolean): Promise<void> {
    if (this.presenter.hasChanges()) {
      useErrorsStore().pushErrorStr(Translator.translateId("recordMustBeSaved"));
      return;
    }
    let docId: string = NamaCommon.getFieldValue(this.presenter.fetchRecord(), CommonFieldIds.ID);
    let req: Record<string, any> = {submissionDocId: docId, onlySelected};
    let result: EInvoiceSignResponse[] | any = await NamaCommon.namaFetch(NamaURLS.UpdateTaxAuthJson, {params: req});
    if (!result || result.length < 1) {
      useErrorsStore().pushErrorStr(Translator.calcByLanguage(
        "الرجاء التأكد من تفعيل أوبشن إرسال المستندات موقعه إلكترونيا الموجود في إعدادت مصلحة الضرائب أو إنك تحاول توقيع سند إلغاء",
        "Please mark option Send Documents With Signature in tax payer config or you try to sign cancel document"));
      return;
    }
    for (let response of result) {
      await this.executeSignCall(response, docId, onlySelected);
    }
  }

  private async executeSignCall(result: EInvoiceSignResponse, id: string, onlySelected: boolean) {
    const eSignLinks = ", please make sure that the e-signer server is running. <br/>You can download the setup file from <a href='https://www.namasoft.com/bin/eSignerSetup.msi'>here</a>"
      + "E SEAL: <br/><a href='https://preprod.invoicing.eta.gov.eg/ItidaWebSign_3.13.000.exe' target='_blank'>ITIDA Web Sign Version 3.11</a>"
      + "<br/><a href='https://egypttrust.com/wp-content/uploads/2023/08/ePass2003__Activation_EgyptTrust.zip' target='_blank'>Egypt Trust Activation</a>"
      + "<br/>MCDR: <a href='http://www.mcdr-ca.com/AWP.aspx'>MCDR Settings</a>"
      + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#block-insecure-private-network-requests</a> is disabled";

    let port = 7994;
    let url = "http://localhost:" + port;
    if (result.einvoiceSignerUrl)
      url = result.einvoiceSignerUrl;
    if (!url.endsWith('/'))
      url += '/';
    url += "signDocuments";
    try {
      let response: any = await NamaCommon.namaFetch(url, {data: result.jsonData, doNotUseMainReqForHttp: true});
      console.log('executeSignCall', response);
      await this.processSignDocsResponse(response, id, onlySelected);
    } catch (e) {
      useErrorsStore().pushErrorStr("There is no connection on port " + port + eSignLinks);
    }
  }

  private async processSignDocsResponse(response: any, id: string, onlySelected: boolean) {
    let req: Record<string, any> = {
      responseBody: typeof response === "string" ? response : JSON.stringify(response),
      submissionDocId: id,
      onlySelected: onlySelected
    };
    let data = await NamaCommon.namaFetch(NamaURLS.SignDocuments, {data: req});
    console.log('processSignDocsResponse', data);
    if (data != null) {
      useErrorsStore().pushErrorStr(data);
      return;
    }
    useMessagesStore().pushMessageStr(Translator.translateId("Documents were signed successfully"));
    await this.presenter.refresh(false, false);
  }

  override async showCurrentApprovalCase() {
    await SimpleEntityMenuItem.showCurrentApprovalCasePopup(this.presenter.fetchRecord().currentApprovalCase);
  }
}
